'use client';

import { useUpgradeRequiredStore } from '@/shared/api/model/upgrade-required.store';
import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';
import {
  modalSurface,
  sheetOverlayTint,
} from '@/shared/ui/shadcn/modal-chrome';

/**
 * Full-screen blocking modal for forced app updates
 * Shown when API returns 426 Upgrade Required
 * Blocks all user interactions until app is refreshed
 */
export function ForceUpdateModal() {
  const { isUpgradeRequired, currentVersion, updateUrl } =
    useUpgradeRequiredStore();
  const t = useTranslations('upgradeRequired');

  if (!isUpgradeRequired) {
    return null;
  }

  const handleRefresh = () => {
    window.location.href = updateUrl || window.location.origin;
  };

  return (
    <div
      className={cn(
        'fixed inset-0 z-[9999] flex items-center justify-center',
        sheetOverlayTint
      )}
    >
      <div className={cn('mx-4 max-w-md rounded-3xl p-8 text-center', modalSurface)}>
        {/* Icon */}
        <div className="mb-6 flex justify-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-warning/15">
            <svg
              className="h-10 w-10 text-warning"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 className="mb-4 text-screen-title text-foreground">
          {t('title')}
        </h2>

        {/* Description */}
        <p className="mb-6 text-muted-foreground">
          {t('description')}
        </p>

        {/* Refresh button */}
        <button
          onClick={handleRefresh}
          className="w-full rounded-lg bg-primary px-6 py-3 font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          {t('refreshNow')}
        </button>

        {/* Version info (small, at bottom) */}
        <p className="mt-6 text-list-item-meta text-muted-foreground/70">
          v{currentVersion}
        </p>
      </div>
    </div>
  );
}
